import { useEffect, useRef, useState, useContext, useMemo, useCallback, memo } from 'react'
import { AssetFile } from 'contentful'

import { ThemeContext } from '@/components/theme-provider'
import { isPrint } from '@/utils/print'
import { cx } from '@/utils/strings'
import SafeHTML from './safe-html'

export interface ImageContentfulProps {
  contentful_id?: string
  title?: string
  description?: string
  publicUrl?: string
  publicUrlDarkMode?: string
  file?: AssetFile
  darkModeImage?: {
    file?: AssetFile
    publicUrl?: string
    svg?: { content: string }
  }
  svg?: { content: string }
  svgDarkMode?: string
  loading?: 'lazy' | 'eager'
  className?: string
  noOptimization?: boolean
  isZoomed?: boolean
  imageShadow?: boolean
  roundedCorners?: boolean
  setAspectRatio?: boolean
}

function ImageContentful({
  contentful_id,
  title,
  description,
  publicUrl: lightModePublicUrl,
  file: lightModeFile,
  svg: lightModeSvg,
  loading = 'lazy',
  darkModeImage,
  noOptimization,
  className,
  isZoomed = false,
  imageShadow = false,
  roundedCorners = false,
  setAspectRatio = true,
  ...rest
}: ImageContentfulProps) {
  const { currentTheme } = useContext(ThemeContext)

  // Direct memoization instead of useState + useEffect to prevent re-renders
  const { file, publicUrl, svg } = useMemo(() => {
    const showDarkImage = currentTheme === 'dark' && darkModeImage
    return {
      file: showDarkImage ? darkModeImage?.file : lightModeFile,
      publicUrl: showDarkImage ? darkModeImage?.publicUrl : lightModePublicUrl,
      svg: showDarkImage ? darkModeImage?.svg : lightModeSvg,
    }
  }, [currentTheme, darkModeImage, lightModeFile, lightModePublicUrl, lightModeSvg])

  const [loadedUrl, setLoadedUrl] = useState('')
  const [isClientEnhanced, setIsClientEnhanced] = useState(false)

  const imgRef = useRef<HTMLImageElement | null>(null)

  const isPrintView = isPrint()
  const currentUrl = publicUrl || file?.url || ''

  // Derive loaded state: true when the current URL has fired onLoad, or in print view
  const loaded = isPrintView || (!!currentUrl && loadedUrl === currentUrl)
  const showLoadingPlaceholder = !isPrintView && isClientEnhanced && !loaded

  // Only enable the placeholder once client-side effects are running. If hydration
  // never happens, SSR markup keeps the image visible instead of leaving it hidden.
  useEffect(() => {
    const img = imgRef.current
    if (img?.complete && img.naturalWidth > 0) {
      setLoadedUrl(currentUrl)
    }

    setIsClientEnhanced(true)
  }, [currentUrl])

  const caption = description ? `${title} - ${description}` : title

  const shouldReturn = !file && !publicUrl && !svg?.content

  const isSvgContent = !!svg?.content

  const shouldUseOptimization = useMemo(
    () => file?.contentType !== 'image/svg+xml' && !noOptimization && !isPrintView,
    [file?.contentType, noOptimization, isPrintView]
  )

  // Always use light mode file dimensions to ensure stable container sizing regardless of active theme.
  // If dark mode image has different (smaller) dimensions, it would otherwise shrink the container.
  const fileHeight = lightModeFile?.details?.image?.height || undefined
  const fileWidth = lightModeFile?.details?.image?.width || undefined
  const { height, width, maxWidth, maxWidthStyle } = useMemo(() => {
    const h = fileHeight || undefined
    const w = fileWidth || undefined
    const checkImgMaxWidth = isZoomed ? 2400 : 1400
    const max = Math.min(w || checkImgMaxWidth, checkImgMaxWidth)
    return { height: h, width: w, maxWidth: max, maxWidthStyle: max ? `calc(min(${max}px, 100%))` : '100%' }
  }, [fileHeight, fileWidth, isZoomed])

  // Memoize the URL transformation function
  const getTransformedUrl = useCallback(
    (w: number, fm: string = 'webp', q: number = 100) => {
      const urlToUse = file?.url
      if (!urlToUse) return ''

      try {
        const url = new URL(urlToUse)
        url.searchParams.set('w', w.toString())
        url.searchParams.set('fm', fm)
        if (Number.isInteger(q) && q < 100 && q > 0) url.searchParams.set('q', q.toString())

        return url.toString()
      } catch {
        return urlToUse
      }
    },
    [file?.url]
  )

  const nonOptimizedSrc = publicUrl || file?.url


  if (shouldReturn) return null

  if (isSvgContent) {
    return (
      <SafeHTML
        as="div"
        html={svg?.content || ''}
        {...rest}
        className={cx('w-full', className)}
        suppressHydrationWarning
      />
    )
  }

  if (!shouldUseOptimization) {
    return (
      <div
        suppressHydrationWarning
        className={cx('relative overflow-hidden block w-full', showLoadingPlaceholder && 'img-placeholder', className)}
        style={{ maxWidth: setAspectRatio ? maxWidthStyle : undefined }}
      >
        <img
          ref={imgRef}
          data-testid="image"
          src={nonOptimizedSrc}
          alt={caption}
          width={width}
          height={height}
          loading={isPrintView ? 'eager' : loading}
          className={cx('w-full h-auto', !isPrintView && 'transition-opacity duration-100 ease-in', showLoadingPlaceholder && 'opacity-0')}
          onLoad={() => setLoadedUrl(currentUrl)}
          {...rest}
        />
      </div>
    )
  }

  return (
    <div
      suppressHydrationWarning
      className={cx(
        'relative overflow-hidden block max-w-full w-full',
        showLoadingPlaceholder && 'img-placeholder',
        className,
        imageShadow && 'shadow-bw-img',
        roundedCorners && 'rounded-lg'
      )}
      style={{ maxWidth: setAspectRatio === true && maxWidthStyle ? maxWidthStyle : undefined }}
      {...rest}
    >
      <picture>
        <source media="(max-width:400px)" srcSet={getTransformedUrl(400, 'avif')} type="image/avif" />
        <source media="(max-width:800px)" srcSet={getTransformedUrl(800, 'avif')} type="image/avif" />
        <source media="(min-width:801px)" srcSet={getTransformedUrl(maxWidth, 'avif')} type="image/avif" />
        <img
          ref={imgRef}
          src={getTransformedUrl(maxWidth, 'jpg')}
          alt={caption}
          width={width}
          height={height}
          loading={loading}
          className={cx('w-full h-auto transition-opacity duration-100 ease-in', showLoadingPlaceholder && 'opacity-0')}
          onLoad={() => setLoadedUrl(currentUrl)}
          data-testid="image"
        />
      </picture>
    </div>
  )
}

export default memo(ImageContentful, (prevProps, nextProps) => {
  // Comprehensive memo comparison to prevent re-renders
  const keys = ['title', 'description', 'publicUrl', 'className', 'loading', 'noOptimization', 'isZoomed'] as const

  for (const key of keys) {
    if (prevProps[key] !== nextProps[key]) {
      return false // Props changed, re-render
    }
  }

  // Check file objects
  if (prevProps.file?.url !== nextProps.file?.url) return false
  if (prevProps.darkModeImage?.file?.url !== nextProps.darkModeImage?.file?.url) return false

  return true // All props equal, skip re-render
})
